import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Rating,
  TextField,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { Loader2, Calendar, Clock, DollarSign, Video, MapPin, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/UI/card';
import { Badge } from '../components/UI/badge';
import { Avatar, AvatarFallback } from '../components/UI/avatar';
import { Button } from '../components/UI/button';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import {
  getMyInvitations,
  unlockInvitationChat,
  lockInvitationChat,
  toggleInvitationChat,
  getInvitationChatThread,
  getInvitationThreadId,
  cancelInvitation
} from '../services/invitationService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getUserData } from '../utils/authUtils';
import { markSessionCompleted } from '../services/sessionService';
import CancellationModal from '../components/Dashboard/CancellationModal';
import SessionCompletionModal from '../components/Dashboard/SessionCompletionModal';

// Define API_URL with explicit port 5000 to match the server
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
console.log('CoffeeChats API_URL initialized as:', API_URL);

const CoffeeChats = () => {
  // Get current user info
  const { currentUser } = useAuth();
  const isProfessional = currentUser?.role === 'professional' || currentUser?.userType === 'professional';
  const navigate = useNavigate();

  // State
  const [coffeeChats, setCoffeeChats] = useState([]);
  const [invitationChats, setInvitationChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    comment: ''
  });
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [currentInvitationId, setCurrentInvitationId] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [chatUnlockStatus, setChatUnlockStatus] = useState({
    success: false,
    error: null
  });
  const [toggleProcessing, setToggleProcessing] = useState(false);

  // Session completion modal state
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [selectedSessionForCompletion, setSelectedSessionForCompletion] = useState(null);
  const [completionLoading, setCompletionLoading] = useState(false);

  // Cancellation modal state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSessionForCancel, setSelectedSessionForCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');

  // History section collapse state
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Fetch coffee chats from API
  useEffect(() => {
    const fetchCoffeeChats = async () => {
      setIsLoading(true);
      setApiError(null);
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication required');
        }
        
        const baseUrl = getApiBaseUrl();
        const sessionsUrl = `${baseUrl}/sessions`;
        
        const response = await axios.get(sessionsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.success) {
          setCoffeeChats(response.data.data.sessions || []);
        } else {
          setCoffeeChats([]);
        }
        
        await fetchInvitationBasedChats();
      } catch (error) {
        console.error('Error fetching coffee chats:', error);
        setApiError(
          error.response?.status === 404 
            ? 'This feature is not yet available. The API endpoint does not exist.'
            : 'Failed to load your coffee chats. Please try again.'
        );
        setCoffeeChats([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCoffeeChats();
  }, []);
  
  // Fetch invitation-based coffee chats
  const fetchInvitationBasedChats = async () => {
    try {
      // Get all invitations (accepted, completed, cancelled) for schedule history
      const result = await getMyInvitations();

      if (result.success && result.data) {
        // Transform invitations to coffee chat format
        // Show ALL statuses: pending, accepted, confirmed, completed, cancelled, etc.
        // This provides a comprehensive history of all coffee chats
        const chats = result.data.invitations
          .filter(inv => ['pending', 'accepted', 'confirmed', 'completed', 'cancelled'].includes(inv.status))
          .map(invitation => {
            // Determine the counterpart (the other person, not the current user)
            const isCurrentUserReceiver = currentUser && invitation.receiver?._id === currentUser._id;
            const counterpart = isCurrentUserReceiver ? invitation.sender : invitation.receiver;

            return {
              id: invitation._id,
              invitationId: invitation._id,
              threadId: invitation.threadId,
              status: invitation.status, // Keep the actual status instead of converting to 'completed'
              scheduledAt: invitation.sessionDetails?.proposedDate,
              duration: invitation.sessionDetails?.duration || 30,
              price: 0,
              professional: {
                _id: counterpart?._id,
                firstName: counterpart?.firstName,
                lastName: counterpart?.lastName,
                profileImage: counterpart?.profileImage,
                title: counterpart?.title,
                company: counterpart?.company
              },
              topics: [invitation.sessionDetails?.topic] || ['Coffee Chat'],
              meetingLink: invitation.sessionDetails?.meetingLocation?.virtualLink || null,
              meetingType: invitation.sessionDetails?.meetingLocation?.type,
              sessionDetails: invitation.sessionDetails,
              sender: invitation.sender,
              receiver: invitation.receiver,
              hasReview: !!invitation.hasReview,
              chatUnlocked: !!invitation.chatUnlocked
            };
          })
          .sort((a, b) => {
            // Sort by scheduled date, latest to earliest
            const dateA = a.scheduledAt ? new Date(a.scheduledAt) : new Date(0);
            const dateB = b.scheduledAt ? new Date(b.scheduledAt) : new Date(0);
            return dateB - dateA; // Descending order (latest first)
          });

        setInvitationChats(chats);
      }
    } catch (error) {
      console.error('Error fetching invitation-based chats:', error);
    }
  };

  // Open review dialog
  const handleLeaveReview = (chatId) => {
    setCurrentChatId(chatId);
    setReviewDialogOpen(true);
  };

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (event, newValue) => {
    setReviewData(prev => ({ ...prev, rating: newValue }));
  };

  const handleSubmitReview = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');
      
      const response = await axios.post(`${API_URL}/sessions/${currentChatId}/feedback`, 
        reviewData, 
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data && response.data.success) {
        setCoffeeChats(prev => prev.map(chat => chat.id === currentChatId ? { ...chat, hasReview: true } : chat));
        setInvitationChats(prev => prev.map(chat => chat.id === currentChatId ? { ...chat, hasReview: true } : chat));
        setReviewDialogOpen(false);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };
  
  const handleToggleChat = async (invitationId) => {
    if (!invitationId) return;
    
    console.log(`🔓 === FRONTEND TOGGLE CHAT START ===`);
    console.log(`Invitation ID: ${invitationId}`);
    console.log(`Current user:`, currentUser);
  
    setToggleProcessing(true);
    setCurrentInvitationId(invitationId);
  
    try {
      console.log(`📡 Calling toggleInvitationChat API...`);
      const result = await toggleInvitationChat(invitationId);
      console.log(`📡 API Response:`, result);
  
      if (result.success) {
        const newChatUnlockedStatus = result.data.chatUnlocked;
        
        // 🔧 FIX: Only update the specific invitation that was actually unlocked
        // This matches the backend behavior and prevents state inconsistency
        setInvitationChats(prev =>
          prev.map(chat =>
            chat.invitationId === invitationId
              ? { ...chat, chatUnlocked: newChatUnlockedStatus }
              : chat
          )
        );
        
        console.log(`✅ Updated chat unlock status for invitation ${invitationId}: ${newChatUnlockedStatus}`);
      } else {
        // This block will now execute and log the server's error message.
        console.error(`[handleToggleChat] API call failed for invitationId ${invitationId}. Server message:`, result.message);
        // We can optionally alert the user that it failed.
        // alert(`Failed to update chat status: ${result.message}`);
      }
    } catch (error) {
      // This will catch any network errors or other exceptions.
      console.error(`[handleToggleChat] A critical error occurred for invitationId ${invitationId}:`, error);
    } finally {
      setToggleProcessing(false);
      setCurrentInvitationId(null);
    }
  };

  const handleUnlockChat = (invitationId) => {
    setCurrentInvitationId(invitationId);
    setUnlockDialogOpen(true);
    setChatUnlockStatus({ success: false, error: null });
  };

  const handleProcessUnlock = async () => {
    setPaymentProcessing(true);
    setChatUnlockStatus({ success: false, error: null });
    
    try {
      const result = await unlockInvitationChat(currentInvitationId);
      
      if (result.success) {
        setInvitationChats(prev => 
          prev.map(chat => 
            chat.invitationId === currentInvitationId
              ? { ...chat, chatUnlocked: true } 
              : chat
          )
        );
        setChatUnlockStatus({ success: true, error: null });
      } else {
        setChatUnlockStatus({ success: false, error: result.message || 'Failed to unlock chat.' });
      }
    } catch (error) {
      setChatUnlockStatus({ success: false, error: error.message || 'An unexpected error occurred.' });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleOpenChat = async (invitationId) => {
    try {
      const result = await getInvitationChatThread(invitationId);
      if (result.success) {
        window.location.href = `/messages?threadId=${result.threadId}`;
      } else {
        alert(result.message || 'Could not find chat thread.');
      }
    } catch (error) {
      alert(error.message || 'An error occurred while trying to open the chat.');
    }
  };

  const handleRescheduleChat = (chatId) => {
    alert(`Reschedule functionality for chat ID: ${chatId} is not yet implemented.`);
  };

  const handleJoinMeeting = (link) => {
    if (!link) {
      toast.error('No meeting link available');
      return;
    }
    // Ensure the link has a protocol
    let fullLink = link;
    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      fullLink = 'https://' + link;
    }
    window.open(fullLink, '_blank', 'noopener,noreferrer');
  };

  // Handle navigating to messages
  const handleJoinChat = async (invitationId) => {
    try {
      const result = await getInvitationThreadId(invitationId);
      if (result.success && result.threadId) {
        navigate(`/messages?thread=${result.threadId}`);
      } else {
        toast.error('Could not find chat thread');
      }
    } catch (error) {
      console.error('Error getting thread ID:', error);
      toast.error('Failed to open chat');
    }
  };

  // Handle cancel invitation
  const handleCancelClick = (session) => {
    setSelectedSessionForCancel(session);
    setCancelDialogOpen(true);
    setCancellationReason('');
  };


  // Handle marking session as completed
  const handleMarkSessionComplete = (session) => {
    console.log('📋 Opening completion modal for session:', session.invitationId);
    setSelectedSessionForCompletion(session);
    setCompletionModalOpen(true);
  };

  // Confirm session completion and capture payment
  const handleConfirmCompletion = async (notes) => {
    if (!selectedSessionForCompletion) return;

    setCompletionLoading(true);

    try {
      console.log('🎯 Marking session as completed:', {
        invitationId: selectedSessionForCompletion.invitationId,
        notes: notes || 'None'
      });

      const result = await markSessionCompleted(
        selectedSessionForCompletion.invitationId,
        notes
      );

      console.log('✅ Session completion response:', result);

      if (result.success) {
        toast.success(`Session completed! Payment of £${(result.data.paymentAmount / 100).toFixed(2)} captured.`, {
          description: `Your earnings: £${(result.data.professionalEarnings / 100).toFixed(2)} will be in your next payout.`,
          duration: 6000
        });

        // Close modal
        setCompletionModalOpen(false);
        setSelectedSessionForCompletion(null);

        // Refresh the list
        await fetchInvitationBasedChats();
      } else {
        toast.error('Failed to complete session', {
          description: result.message || 'Please try again later'
        });
      }
    } catch (error) {
      console.error('❌ Error completing session:', error);

      // Handle specific error codes
      if (error.code === 'FORBIDDEN') {
        toast.error('Only the professional can mark sessions as completed');
      } else if (error.code === 'ALREADY_COMPLETED') {
        toast.error('This session has already been completed');
      } else if (error.code === 'PAYMENT_EXPIRED') {
        toast.error('Payment authorization has expired (>7 days)');
      } else if (error.code === 'NO_PAYMENT') {
        toast.error('No authorized payment found for this session');
      } else {
        toast.error('Failed to complete session', {
          description: error.message || 'Please try again later'
        });
      }
    } finally {
      setCompletionLoading(false);
    }
  };

  // Close completion modal
  const handleCloseCompletionModal = () => {
    if (!completionLoading) {
      setCompletionModalOpen(false);
      setSelectedSessionForCompletion(null);
    }
  };

  // Split sessions into accepted and other statuses
  const acceptedSessions = invitationChats.filter(chat => ['accepted', 'confirmed'].includes(chat.status));
  const historySessions = invitationChats.filter(chat => !['accepted', 'confirmed'].includes(chat.status));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ padding: '32px 16px' }}>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-3xl font-normal mb-8 text-gray-900 dark:text-white">Coffee Chats</h1>

        {isLoading && (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        )}

        {apiError && !isLoading && (
          <Alert severity="error" className="mt-4">{apiError}</Alert>
        )}

        {!isLoading && !apiError && (
          <>
            {invitationChats.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg text-gray-600 dark:text-gray-400">No coffee chats scheduled.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Accepted Sessions Section */}
                {acceptedSessions.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl mb-4 text-gray-900 dark:text-white">Confirmed Coffee Chats</h2>
                    <div className="space-y-4">
                      {acceptedSessions.map((session) => {
                        const professionalName = session.professional?.name ||
                          `${session.professional?.firstName || ''} ${session.professional?.lastName || ''}`.trim() ||
                          'Professional';

                        const scheduledTime = session.scheduledAt ? new Date(session.scheduledAt) : new Date();
                        const formattedDate = scheduledTime.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        });
                        const formattedTime = scheduledTime.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        });

                        const getStatusColor = (status) => {
                          switch (status) {
                            case 'completed':
                              return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
                            case 'cancelled':
                              return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
                            case 'pending':
                              return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
                            case 'accepted':
                            case 'confirmed':
                              return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
                            default:
                              return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
                          }
                        };

                        const userId = getUserData()?._id;
                        const isInvitationSender = session.sender?._id === userId;
                        const isVirtualMeeting = session.meetingType === 'virtual';
                        const hasMeetingLink = session.meetingLink;

                        return (
                          <Card key={session.id}>
                            <CardContent className="p-6">
                              <div className="flex items-start gap-4">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-blue-600 text-white">
                                    {professionalName.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between mb-1">
                                    <h3 className="text-lg text-gray-900 dark:text-white">{professionalName}</h3>
                                    <Badge className={`${getStatusColor(session.status)} flex-shrink-0 ml-2`}>
                                      {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {session.professional?.title || session.professional?.position || ''}
                                    {session.professional?.company && ` at ${session.professional.company}`}
                                  </p>
                                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="h-4 w-4" />
                                      {formattedDate}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-4 w-4" />
                                      {formattedTime}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-4 w-4" />
                                      {session.duration || 30} min
                                    </div>
                                    {session.meetingLink ? (
                                      <div className="flex items-center gap-1">
                                        <Video className="h-4 w-4" />
                                        Virtual
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <MapPin className="h-4 w-4" />
                                        In-person
                                      </div>
                                    )}
                                  </div>
                                  {/* Action Buttons at Bottom */}
                                  <div className="flex flex-wrap gap-2 mt-4">
                                    {isVirtualMeeting && hasMeetingLink && (
                                      <Button
                                        variant="default"
                                        size="sm"
                                        onClick={() => handleJoinMeeting(session.meetingLink)}
                                      >
                                        <Video className="h-4 w-4 mr-1" />
                                        Join Meeting
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleJoinChat(session.invitationId)}
                                    >
                                      Messages
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleCancelClick(session)}
                                      className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-500 dark:hover:bg-orange-950"
                                    >
                                      Cancel
                                    </Button>
                                    {isProfessional && !isInvitationSender && session.status !== 'completed' && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleMarkSessionComplete(session)}
                                        className="border-green-500 text-green-600 hover:bg-green-50 dark:bg-gray-800 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-950 font-semibold"
                                      >
                                        <CheckCircle className="h-4 w-4 mr-1" />
                                        Mark as Completed
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* History Section */}
                {historySessions.length > 0 && (
                  <div>
                    <div
                      className="flex items-center gap-2 mb-4 cursor-pointer hover:opacity-70 transition-opacity"
                      onClick={() => setHistoryExpanded(!historyExpanded)}
                    >
                      <h2 className="text-2xl text-gray-900 dark:text-white">
                        History ({historySessions.length})
                      </h2>
                      {historyExpanded ? (
                        <ChevronUp className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                      ) : (
                        <ChevronDown className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                      )}
                    </div>
                    {historyExpanded && (
                    <div className="space-y-4">
                      {historySessions.map((session) => {
                        const professionalName = session.professional?.name ||
                          `${session.professional?.firstName || ''} ${session.professional?.lastName || ''}`.trim() ||
                          'Professional';

                        const scheduledTime = session.scheduledAt ? new Date(session.scheduledAt) : new Date();
                        const formattedDate = scheduledTime.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        });
                        const formattedTime = scheduledTime.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        });

                        const getStatusColor = (status) => {
                          switch (status) {
                            case 'completed':
                              return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
                            case 'cancelled':
                              return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
                            case 'pending':
                              return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
                            case 'accepted':
                            case 'confirmed':
                              return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
                            default:
                              return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
                          }
                        };

                        return (
                          <Card key={session.id}>
                            <CardContent className="p-6">
                              <div className="flex items-start gap-4">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-blue-600 text-white">
                                    {professionalName.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between mb-1">
                                    <h3 className="text-lg text-gray-900 dark:text-white">{professionalName}</h3>
                                    <Badge className={`${getStatusColor(session.status)} flex-shrink-0 ml-2`}>
                                      {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {session.professional?.title || session.professional?.position || ''}
                                    {session.professional?.company && ` at ${session.professional.company}`}
                                  </p>
                                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="h-4 w-4" />
                                      {formattedDate}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-4 w-4" />
                                      {formattedTime}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-4 w-4" />
                                      {session.duration || 30} min
                                    </div>
                                    {session.meetingLink ? (
                                      <div className="flex items-center gap-1">
                                        <Video className="h-4 w-4" />
                                        Virtual
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <MapPin className="h-4 w-4" />
                                        In-person
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}

      {/* Cancellation Modal */}
      <CancellationModal
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        session={selectedSessionForCancel}
        userRole={isProfessional ? 'Professional' : 'Seeker'}
        onConfirm={async (invitationId, reason) => {
          await cancelInvitation(invitationId, reason);
          toast.success('Session cancelled successfully. Refund will be processed within 5-10 business days.');
          await fetchInvitationBasedChats();
          setCancelDialogOpen(false);
          setSelectedSessionForCancel(null);
        }}
      />

      {/* Session Completion Modal */}
      <SessionCompletionModal
        open={completionModalOpen}
        onClose={handleCloseCompletionModal}
        session={selectedSessionForCompletion}
        onConfirm={handleConfirmCompletion}
        loading={completionLoading}
      />

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)}>
        <DialogTitle>Leave a Review</DialogTitle>
        <DialogContent>
          <Rating
            name="rating"
            value={reviewData.rating}
            onChange={handleRatingChange}
          />
          <TextField
            autoFocus
            margin="dense"
            name="comment"
            label="Comment"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={reviewData.comment}
            onChange={handleReviewChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmitReview}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Unlock Chat Dialog */}
      <Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)}>
        <DialogTitle>Unlock Chat</DialogTitle>
        <DialogContent>
          {paymentProcessing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Loader2 className="h-6 w-6 animate-spin" />
              <Typography>Processing your request...</Typography>
            </Box>
          ) : chatUnlockStatus.success ? (
            <Alert severity="success">Chat unlocked successfully! You can now message the professional.</Alert>
          ) : chatUnlockStatus.error ? (
            <Alert severity="error">{chatUnlockStatus.error}</Alert>
          ) : (
            <Typography>
              Unlocking this chat will allow you to continue the conversation with the professional. This may require a payment in a production environment.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialogOpen(false)}>
            {chatUnlockStatus.success ? 'Close' : 'Cancel'}
          </Button>
          {!chatUnlockStatus.success && (
            <Button onClick={handleProcessUnlock} disabled={paymentProcessing}>
              Confirm Unlock
            </Button>
          )}
        </DialogActions>
      </Dialog>
      </div>
    </div>
  );
};

export default CoffeeChats; 